import {
  Component,
  h,
  Prop,
  Element,
  Host,
  Fragment,
  Watch,
} from '@stencil/core';
import { responsive } from '../../responsive/responsive';
import store from './store';
import { fetchPropertyData } from './fetch-realestate-data';

@Component({
  tag: 'ui-realestate-search',
  styleUrl: 'ui-realestate-search.scss',
  shadow: false,
})
export class UiRealEstateSearch {
  @Element() el: HTMLUiRealestateSearchElement;

  @Prop() headline: string;

  @Prop() text: string;

  @Prop() image: string;

  @Prop() imageAltText: string;

  @Prop() imageCopyright: string;

  @Prop() imageMouseoverText: string;

  @Prop() usageTypeId: string;

  @Prop() resultsPerPage: number;

  @Prop() apiUrl: string;

  @Prop() apiKey: string;

  @Prop() apiVersion: string;

  @Prop() replace?: string;

  @Prop() sizes?: string;

  @Prop({ reflect: true }) componentWidth: number;

  @Prop() i18n: any;

  @Prop() noResultMessageTitle?: string;

  @Prop() noResultMessageHtml?: string;

  @Prop() realEstatePagePath: string;

  offcanvasId = 'realestate-filters';

  async componentWillLoad() {
    if (this.apiKey && this.apiUrl && this.apiVersion)
      this.fetchFiltersAndResults();
    // save i18n labels and error messages on store
    if (this.i18n) store.state.i18n = this.i18n;
    if (this.noResultMessageTitle)
      store.state.noResultMessageTitle = this.noResultMessageTitle;
    if (this.noResultMessageHtml)
      store.state.noResultMessageHtml = this.noResultMessageHtml;
    // save realEstatePagePath on store
    if (this.realEstatePagePath)
      store.state.realEstatePagePath = this.realEstatePagePath;
  }

  componentDidLoad() {
    responsive(this.el);
    // save resultsPerPage data on store
    if (this.resultsPerPage) store.state.resultsPerPage = this.resultsPerPage;
  }

  @Watch('componentWidth')
  onComponentWidthChange() {
    store.state.isMobile = this.componentWidth < 1024;
  }

  /**
   * Fetch initial filters and search results.
   * If usageTypeId is set on backend, remove usage from the list of filters and update store.
   */
  async fetchFiltersAndResults() {
    const usageTypeIsSetViaProp = this.usageTypeId;
    // update selected filters and fetch
    if (usageTypeIsSetViaProp)
      store.state.selectedFilters.usage = usageTypeIsSetViaProp;
    const filters = await this.fetchDataHandler('filters');
    // remove usage from the list of filters if is set
    store.state.filterGroups = usageTypeIsSetViaProp
      ? filters.filter((el) => el.name !== 'usage')
      : [...filters];
    // fetch initial search results
    store.state.searchResults = await this.fetchDataHandler('search');
  }

  private async fetchDataHandler(actionType) {
    const data = await fetchPropertyData({
      actionType,
      apiUrl: this.apiUrl,
      apiKey: this.apiKey,
      apiVersion: this.apiVersion,
    });
    return actionType === 'filters' ? data.filterGroups : data.results;
  }

  render() {
    return (
      <Host>
        <div
          class={{
            'realestate-filters': true,
            mobile: store.state.isMobile,
            hasBgImage: this.image !== '',
          }}
        >
          <slot name="media">
            {this.image && (
              <ui-image
                src={this.image}
                altText={this.imageAltText}
                copyrightText={this.imageCopyright}
                mouseoverText={this.imageMouseoverText}
                replace={this.replace}
                sizes={this.sizes}
              ></ui-image>
            )}
          </slot>
          <div class="filter-wrapper">
            <div class="realestate-info">
              <ui-headline level={2}>{this.headline}</ui-headline>
              {this.text && <ui-paragraph innerHTML={this.text}></ui-paragraph>}
            </div>
            {store.state.isMobile && (
              <Fragment>
                <ui-offcanvas-trigger offcanvas-id={this.offcanvasId}>
                  <ui-button>{store.state.i18n.filters || 'Filters'}</ui-button>
                </ui-offcanvas-trigger>
                <ui-offcanvas-panel id={this.offcanvasId} darken close-outside>
                  <ui-container variant={'secondary100'}>
                    <ui-offcanvas-trigger offcanvas-id={this.offcanvasId}>
                      <ui-button
                        class="search-facets-panel__link"
                        variant="plain"
                        icon="close"
                      >
                        {store.state.i18n.closeFilters || 'Filter schließen'}
                      </ui-button>
                    </ui-offcanvas-trigger>
                    <ui-realestate-search-filters
                      apiKey={this.apiKey}
                      apiVersion={this.apiVersion}
                      apiUrl={this.apiUrl}
                      usageTypeId={this.usageTypeId}
                    ></ui-realestate-search-filters>
                  </ui-container>
                </ui-offcanvas-panel>
              </Fragment>
            )}
            {!store.state.isMobile && (
              <ui-realestate-search-filters
                apiKey={this.apiKey}
                apiVersion={this.apiVersion}
                apiUrl={this.apiUrl}
                usageTypeId={this.usageTypeId}
              ></ui-realestate-search-filters>
            )}
          </div>
        </div>
        {store.state.searchResults.totalResults ? (
          <ui-realestate-property-view></ui-realestate-property-view>
        ) : (
          <sd-spinner style={{ fontSize: '2rem' }} color="primary"></sd-spinner>
        )}
      </Host>
    );
  }
}
